import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';

import { getRouteStackPath } from '@portal/config/routes';

import { translate } from '@portal/services/i18n';
import { maskPhoneInput, unmaskField } from '@portal/services/masks';
import { Col } from 'antd';

import { UserActions } from '@portal/actions';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import * as MessageService from '@portal/services/message';
import { validateEmail } from '@portal/utils/validate';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { validatePhone } from 'validations-br';

const EditMultiAccessUser = () => {
  const dispatch = useDispatch();
  const {
    user: { updateUserStatus },
  } = useReduxState();
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const navigate = useNavigation<{ userInfo: models.AccountMultiAccess }>();
  const [account, setAccount] = useState<models.AccountMultiAccess>({
    name: '',
    email: '',
    cellphone: '',
  });

  useEffect(() => {
    if (navigate?.location?.state?.userInfo) {
      const loadedAccount = navigate?.location?.state?.userInfo;

      if (loadedAccount?.cellphone?.startsWith('55')) {
        loadedAccount.cellphone = loadedAccount.cellphone.substring(2);
      }

      setAccount(loadedAccount);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate?.location?.state?.userInfo]);

  const handleValidate = () => {
    if (!validateEmail(account.email)) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.EMAIL_INVALID',
        { domain: 'validation' }
      );
    }

    if (!validatePhone(account.cellphone)) {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CELLPHONE',
        { domain: 'validation' }
      );
    }

    setAccount({
      ...account,
      cellphone: unmaskField(account.cellphone),
      email: account.email,
    });

    setIsPinModalVisible(true);
  };

  const handleConfirmation = async (pin: string) => {
    setIsPinModalVisible(false);

    const encryptedPIN = encrypt(pin);
    const data = {
      authenticate: {
        pin: encryptedPIN,
        timestamp: new Date().getTime().toString(),
      },
      cellphone: `55${account.cellphone}`,
      email: account.email ?? '',
    };

    if (account.id) {
      dispatch(UserActions.updateUser(account.id, data));
    }
  };

  useEffect(() => {
    if (updateUserStatus) {
      MessageService.info(
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.EDIT_USER_INFO.SUCCESS'
      );

      navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
    }
  }, [navigate, updateUserStatus]);

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
            url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
          },
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.EDIT_USER_INFO.TITLE'
            ),
          },
        ]}
      />
      <div className="multiaccess-page">
        <AdvancedPanelContentHeader
          title={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.EDIT_USER_INFO.TITLE'
          )}
          description={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.EDIT_USER_INFO.DESCRIPTION'
          )}
          testId="multiaccess-edit-user-header"
          goBackAction={() => navigate.back()}
        />
        <AdvancedCard testId="create-account-user__card">
          <Col className="create-account-user__col create-account-user__col-margin-top">
            <AdvancedInput
              label={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.EDIT_USER_INFO.EMAIL'
              )}
              variant="standard"
              value={account?.email}
              onChange={(value) => {
                setAccount({ ...account, email: value });
              }}
            />
          </Col>
          <Col className="create-account-user__col create-account-user__col-margin-top">
            <AdvancedInput
              label={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.EDIT_USER_INFO.CELLPHONE'
              )}
              variant="standard"
              value={maskPhoneInput(account?.cellphone)}
              onChange={(value) => {
                setAccount({ ...account, cellphone: unmaskField(value) });
              }}
            />
          </Col>
          <div className="multiaccess-page__button">
            <AdvancedButton
              size="large"
              fullWidth
              text={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.BUTTON')}
              disabled={!account.email || !account.cellphone}
              onClick={handleValidate}
            />
          </div>
        </AdvancedCard>
        <ConfirmationModal
          visible={isPinModalVisible}
          setVisible={setIsPinModalVisible}
          handleConfirmation={handleConfirmation}
          text={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TEXT')}
          title={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TITLE')}
        />
      </div>
    </>
  );
};

export default EditMultiAccessUser;
